import type { ReactElement } from 'react';
import { useState, useRef, useEffect } from 'react';
import { useUID } from 'react-uid';
import { HeapEvent } from 'config';
import { Component as CaretIcon } from 'icons/icon-ui--caret.svg';

type Props = {
  selectedValue: string | number;
  values: string[] | number[];
  direction?: 'downwards' | 'upwards';
  onSelect: (value: string | number) => void;
  heapEventDropdown?: HeapEvent | null;
  heapEventDropdownElement?: HeapEvent | null;
};

function Dropdown({
  selectedValue,
  values,
  direction = 'downwards',
  onSelect,
  heapEventDropdown = null,
  heapEventDropdownElement = null,
}: Props): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [listStyle, setListStyle] = useState({});
  const listElem = useRef(null);
  const uid = useUID();

  const unexpandIfFocusedOutside = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setExpanded(false);
    }
  };

  useEffect(() => {
    if (direction === 'upwards') {
      // @ts-expect-error TODO: fix null safety
      setListStyle({ top: `-${listElem.current.offsetHeight}px` });
    }
  }, [expanded, direction]);

  return (
    <div className="c-input-group c-input-group--icon-right">
      <div
        className={[
          'c-select',
          heapEventDropdown,
          expanded ? 'is-active' : '',
        ].join(' ')}
        role="listbox"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onBlur={unexpandIfFocusedOutside}
        onKeyPress={(e) => e.key === 'Enter' && setExpanded(true)}
      >
        <div
          className="c-select__value"
          role="option"
          aria-selected="true"
        >
          <div className="c-select__item">
            <div className="c-select__heading">
              <div className="c-select__title">{selectedValue}</div>
            </div>
          </div>
        </div>
        <div ref={listElem} className="c-select__list" style={listStyle}>
          {values.map((value, index) => (
            <div
              key={`select-${uid}-${value}`}
              id={`select-${uid}-${value}`}
              className={[
                'c-select__item-wrapper',
                heapEventDropdownElement,
                selectedValue === value ? 'is-selected' : '',
                index === hoveredIndex ? 'is-hovered' : '',
              ].join(' ')}
              role="option"
              aria-selected={selectedValue === value}
              tabIndex={0}
              onClick={() => onSelect(value)}
              onPointerEnter={() => setHoveredIndex(index)}
              onPointerLeave={() => setHoveredIndex(-1)}
              onKeyPress={() => onSelect(value)}
            >
              <div className="c-select__item">
                <div className="c-select__heading">
                  <div className="c-select__title">{value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <select
        aria-label="quantity"
        value={selectedValue}
        onChange={(e) => onSelect(typeof selectedValue === 'number'
          ? Number.parseFloat(e.target.value)
          : e.target.value)}
      >
        {values.map((value) => (
          <option
            key={`option-${uid}-${value}`}
            id={`option-${uid}-${value}`}
            value={value}
          >
            {value}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="c-link c-input-icon c-input-icon--right"
      >
        <CaretIcon
          className="c-select__caret-icon c-svg-fill-gray-115-color"
          width="12"
          height="12"
        />
      </button>
    </div>
  );
}

export default Dropdown;
